import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  tokens,
  mergeClasses,
  useId,
} from '@fluentui/react-components'
import { Dismiss24Regular } from '@fluentui/react-icons'
import { useState } from 'react'

import { ErrorMessageBar } from '../../../../../components/ErrorMessageBar'
import { playgroundsApi } from '../../../../../data/api/playgrounds.api'
import type { WorkspaceItem } from '../../../../../shared/models/WorkspaceItem'

import {
  WorkspaceExplorerMode,
  WorkspaceExplorerSelectionMode,
} from './enums/WorkspaceExplorerMode'
import {
  useWorkspaceExplorer,
  WorkspaceExplorerProvider,
} from './providers/workspaceExplorerProvider'
import WorkspaceExplorerBreadcrumb from './WorkspaceExplorerBreadcrumb'
import WorkspaceExplorerItemsTable from './WorkspaceExplorerItemsTable'
import WorkspaceExplorerUpload from './WorkspaceExplorerUpload'
import WorkspaceExplorerUploadToPlayground from './WorkspaceExplorerUploadToPlayground'

const useStyles = makeStyles({
  root: {
    maxWidth: '800px',
    // height: '640px',
    // maxHeight: 'calc(100dvh - 48px)',
    maxHeight: '640px',
    display: 'flex',
    flexDirection: 'column',
  },
  rootHeightLarge: {
    height: '640px',
    // maxHeight: '640px',
  },

  body: {
    flexGrow: 1,
    minHeight: 0,
    gridTemplateRows: 'auto minmax(0, 1fr) auto',
  },
  content: {
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    gap: tokens.spacingVerticalM,
  },
  contentWithItemsTable: {
    overflow: 'hidden',
  },
  breadcrumb: {
    flexWrap: 'wrap',
    rowGap: tokens.spacingVerticalXS,
    minWidth: 0,
  },

  breadcrumbItem: {
    minWidth: 0,
  },
  breadcrumbButton: {
    minWidth: 0,
    maxWidth: '100%',
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
  },
  breadcrumbLabel: {
    display: 'block',
    minWidth: 0,
    maxWidth: '240px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
})

interface WorkspaceExplorerProps {
  open: boolean
  contentMode?: 'dialog' | 'content'
  onConfirm: (data: {
    workspaceId: string
    selectedItem?: WorkspaceItem
    statusRequest?: boolean
  }) => void
  onClose?: () => void
  // onSelectionChange: (hasSelection: boolean) => void
  workspaceId: string
  currentItemId?: string
  sourceParentId?: string | null
  mode: WorkspaceExplorerMode
  parentId?: string | null
}

function WorkspaceExplorerInner({
  mode,
  workspaceId,
  parentId,
  formId,
  onConfirm,
  onSelectionChange,
  onSelectionPlaygroundModeChange,
}: Pick<WorkspaceExplorerProps, 'mode' | 'workspaceId' | 'parentId'> & {
  formId: string
  onConfirm: (data: {
    workspaceId: string
    selectedItem?: WorkspaceItem
    statusRequest?: boolean
  }) => void
  onSelectionChange: (hasSelection: boolean) => void
  onSelectionPlaygroundModeChange: (mode: WorkspaceExplorerSelectionMode | undefined) => void
}) {
  return (
    <>
      {mode === WorkspaceExplorerMode.Upload && (
        <WorkspaceExplorerUpload
          workspaceId={workspaceId}
          parentId={parentId ?? null}
          onConfirm={onConfirm}
        />
      )}
      {mode === WorkspaceExplorerMode.UploadToImagePlayground && (
        <WorkspaceExplorerUploadToPlayground
          workspaceId={workspaceId}
          parentId={parentId ?? null}
          formId={formId}
          onConfirm={onConfirm}
          onSelectionChange={onSelectionChange}
          onSelectionModeChange={onSelectionPlaygroundModeChange}
        />
      )}
      {(mode === WorkspaceExplorerMode.Copy ||
        mode === WorkspaceExplorerMode.Move ||
        mode === WorkspaceExplorerMode.Select) && (
        <>
          <WorkspaceExplorerBreadcrumb />
          <WorkspaceExplorerItemsTable />
        </>
      )}
    </>
  )
}

export function WorkspaceExplorerDialog({
  open,
  onConfirm,
  onClose,
  workspaceId,
  currentItemId,
  sourceParentId,
  mode,
  contentMode = 'dialog',
  parentId,
}: WorkspaceExplorerProps & { currentItemId?: string }) {
  const styles = useStyles()

  const uploadFormId = useId()

  const { moveItem, copyItem, currentParentId, selectedItem, items, isLoading, isError } =
    useWorkspaceExplorer()
  const selectedFile =
    !isLoading && !isError
      ? items.find((item) => item.id === selectedItem?.id && item.type === 'file')
      : undefined

  const [hasWorkspaceSelection, setHasWorkspaceSelection] = useState(false)
  const [selectionPlaygroundMode, setSelectionPlaygroundMode] = useState<
    WorkspaceExplorerSelectionMode | undefined
  >(undefined)

  const handleSelectionPlaygroundModeChange = (
    mode: WorkspaceExplorerSelectionMode | undefined,
  ) => {
    setSelectionPlaygroundMode(mode)
  }

  if (!workspaceId) {
    return <ErrorMessageBar title="Workspace not found" message="A workspace ID is required." />
  }

  const handlerConfirm = async (fileFromWorkspace?: WorkspaceItem, statusRequest?: boolean) => {
    if (
      (mode === WorkspaceExplorerMode.Move || mode === WorkspaceExplorerMode.Copy) &&
      !currentItemId
    ) {
      return
    }

    switch (mode) {
      case WorkspaceExplorerMode.Move: {
        if (!currentItemId || sourceParentId === undefined) {
          return
        }

        moveItem(currentItemId, currentParentId, sourceParentId)
        onConfirm({ workspaceId })
        break
      }
      case WorkspaceExplorerMode.Copy: {
        if (!currentItemId || sourceParentId === undefined) {
          return
        }
        const result = await copyItem(currentItemId, currentParentId, sourceParentId)
        onConfirm({ workspaceId, statusRequest: result.success })
        break
      }
      case WorkspaceExplorerMode.Select: {
        if (!selectedFile) {
          return
        }
        onConfirm({ workspaceId, selectedItem: selectedFile })
        break
      }
      case WorkspaceExplorerMode.UploadToImagePlayground: {
        //find parentId of playground/uploaded of current worksapce
        const { data } = await playgroundsApi.referenceFolder(workspaceId)

        let resultObj: { success: boolean; item: WorkspaceItem | undefined } = {
          success: false,
          item: undefined,
        }

        if (selectionPlaygroundMode === WorkspaceExplorerSelectionMode.SelectFromWorkspace) {
          if (!fileFromWorkspace) {
            return
          }

          resultObj = await copyItem(
            fileFromWorkspace.id,
            data.parentId,
            fileFromWorkspace.parentId,
            true,
          )
          onConfirm({ workspaceId, selectedItem: resultObj.item, statusRequest: resultObj.success })
        } else {
          resultObj.success = statusRequest ?? false
          onConfirm({
            workspaceId,
            selectedItem: fileFromWorkspace,
            statusRequest: resultObj.success,
          })
        }

        break
      }
      default:
        break
    }
  }
  const content = (
    <WorkspaceExplorerInner
      mode={mode}
      workspaceId={workspaceId}
      parentId={parentId}
      formId={uploadFormId}
      onSelectionChange={setHasWorkspaceSelection}
      onConfirm={({ selectedItem, statusRequest }) => handlerConfirm(selectedItem, statusRequest)}
      onSelectionPlaygroundModeChange={handleSelectionPlaygroundModeChange}
    />
  )

  const confirmAction =
    mode !== WorkspaceExplorerMode.Upload &&
    (mode === WorkspaceExplorerMode.UploadToImagePlayground ? (
      selectionPlaygroundMode === WorkspaceExplorerSelectionMode.SelectFromWorkspace && (
        <Button
          appearance="primary"
          type="submit"
          form={uploadFormId}
          disabled={!hasWorkspaceSelection}
        >
          Upload
        </Button>
      )
    ) : (
      <Button
        onClick={() => handlerConfirm()}
        appearance="primary"
        disabled={mode === WorkspaceExplorerMode.Select && !selectedFile}
      >
        {mode === WorkspaceExplorerMode.Select ? 'Select' : 'Apply'}
      </Button>
    ))

  const isSelectingWorkspaceItems =
    mode === WorkspaceExplorerMode.Copy ||
    mode === WorkspaceExplorerMode.Move ||
    mode === WorkspaceExplorerMode.Select ||
    (mode === WorkspaceExplorerMode.UploadToImagePlayground &&
      selectionPlaygroundMode === WorkspaceExplorerSelectionMode.SelectFromWorkspace)

  return (
    <>
      {contentMode === 'dialog' && (
        <Dialog
          open={open}
          onOpenChange={(_event, data) => {
            if (!data.open) {
              onClose?.()
            }
          }}
        >
          <DialogSurface
            className={mergeClasses(
              styles.root,
              mode !== WorkspaceExplorerMode.Upload &&
                (mode !== WorkspaceExplorerMode.UploadToImagePlayground ||
                  selectionPlaygroundMode === WorkspaceExplorerSelectionMode.SelectFromWorkspace) &&
                styles.rootHeightLarge,
            )}
          >
            <DialogBody className={styles.body}>
              <DialogTitle
                action={
                  <>
                    {/* <p>mode: {mode}</p>
                  <p>selectionPlaygroundMode: {selectionPlaygroundMode}</p> */}
                    <Button
                      appearance="subtle"
                      aria-label="close"
                      icon={<Dismiss24Regular />}
                      onClick={onClose}
                    />
                  </>
                }
              >
                {(() => {
                  switch (mode) {
                    case WorkspaceExplorerMode.Move:
                      return 'Move to '
                    case WorkspaceExplorerMode.Copy:
                      return 'Copy to '
                    case WorkspaceExplorerMode.Select:
                      return 'Select from Workspace'
                    case WorkspaceExplorerMode.Upload:
                      return 'Upload files'
                    case WorkspaceExplorerMode.UploadToImagePlayground:
                      switch (selectionPlaygroundMode) {
                        case WorkspaceExplorerSelectionMode.UploadFile:
                          return 'Upload file'
                        case WorkspaceExplorerSelectionMode.SelectFromWorkspace:
                          return 'Select from Workspace'
                        default:
                          return 'Attach files'
                      }
                    default:
                      return ''
                  }
                })()}
              </DialogTitle>
              <DialogContent
                className={mergeClasses(
                  styles.content,
                  isSelectingWorkspaceItems && styles.contentWithItemsTable,
                )}
              >
                {content}
              </DialogContent>
              {mode !== WorkspaceExplorerMode.Upload && (
                <DialogActions>{confirmAction}</DialogActions>
              )}
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
      {contentMode === 'content' && (
        <div
          className={mergeClasses(
            styles.content,
            isSelectingWorkspaceItems && styles.contentWithItemsTable,
          )}
        >
          {content}
          <div>
            {onClose && <Button onClick={onClose}>Cancel</Button>}
            {confirmAction}
          </div>
        </div>
      )}
    </>
  )
}

export default function WorkspaceExplorer(props: WorkspaceExplorerProps) {
  if (!props.open) {
    return null
  }

  if (!props.workspaceId) {
    return <ErrorMessageBar title="Workspace not found" message="A workspace ID is required." />
  }

  return (
    <WorkspaceExplorerProvider
      key={`${props.workspaceId}:${props.mode}`}
      workspaceId={props.workspaceId}
      mode={props.mode}
      contentMode={props.contentMode}
    >
      <WorkspaceExplorerDialog {...props} />
    </WorkspaceExplorerProvider>
  )
}
